import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";

const AuthContext = createContext(null);

function getAdminEmails() {
  return (process.env.REACT_APP_ADMIN_EMAILS || "")
    .split(",")
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubUserDoc = null;
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user || null);

      if (unsubUserDoc) {
        unsubUserDoc();
        unsubUserDoc = null;
      }

      if (!user) {
        setUserData(null);
        setLoading(false);
        return;
      }

      const adminEmails = getAdminEmails();
      const isEnvAdmin = adminEmails.includes((user.email || "").toLowerCase());
      const userRef = doc(db, "users", user.uid);
      const existingSnap = await getDoc(userRef);
      const existingData = existingSnap.exists() ? existingSnap.data() : {};
      const existingRole = existingSnap.exists() ? existingSnap.data()?.role : null;
      const roleToStore = isEnvAdmin ? "admin" : (existingRole || "user");

      await setDoc(
        userRef,
        {
          uid: user.uid,
          email: user.email || "",
          name: existingData?.name || user.displayName || "",
          username: existingData?.username || existingData?.name || user.displayName || "",
          mobile: existingData?.mobile || "",
          phone: existingData?.phone || existingData?.mobile || "",
          age: existingData?.age || "",
          gender: existingData?.gender || "",
          city: existingData?.city || "",
          occupation: existingData?.occupation || "",
          address: existingData?.address || "",
          photoURL: user.photoURL || "",
          role: roleToStore,
          lastLoginAt: serverTimestamp(),
          createdAt: existingSnap.exists() ? existingSnap.data()?.createdAt || serverTimestamp() : serverTimestamp(),
        },
        { merge: true }
      );

      unsubUserDoc = onSnapshot(userRef, (snap) => {
        const data = snap.exists() ? snap.data() : null;
        if (!data) {
          setUserData(null);
          setLoading(false);
          return;
        }

        const mergedRole = isEnvAdmin ? "admin" : data.role || "user";
        setUserData({ ...data, role: mergedRole });
        setLoading(false);
      });
    });

    return () => {
      if (unsubUserDoc) unsubUserDoc();
      unsubAuth();
    };
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      userData,
      loading,
      signInWithGoogle: () => signInWithPopup(auth, googleProvider),
      signInWithEmail: (email, password) => signInWithEmailAndPassword(auth, email, password),
      signUpWithEmail: async (email, password, profileData = {}) => {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        const user = cred.user;
        const adminEmails = getAdminEmails();
        const isEnvAdmin = adminEmails.includes((user.email || "").toLowerCase());

        const cleanName = (profileData.name || "").trim();
        if (cleanName) {
          await updateProfile(user, { displayName: cleanName });
        }

        await setDoc(
          doc(db, "users", user.uid),
          {
            uid: user.uid,
            email: user.email || email,
            name: cleanName,
            username: cleanName,
            mobile: (profileData.mobile || "").trim(),
            phone: (profileData.mobile || profileData.phone || "").trim(),
            age: profileData.age || "",
            gender: profileData.gender || "",
            city: (profileData.city || "").trim(),
            occupation: (profileData.occupation || "").trim(),
            address: (profileData.address || "").trim(),
            photoURL: user.photoURL || "",
            role: isEnvAdmin ? "admin" : "user",
            createdAt: serverTimestamp(),
            lastLoginAt: serverTimestamp(),
          },
          { merge: true }
        );

        return cred;
      },
      resetPassword: (email) => sendPasswordResetEmail(auth, email),
      logout: () => signOut(auth),
      isAdmin: userData?.role === "admin",
    }),
    [currentUser, userData, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
